import { Col, Input, Modal, Row, Spin, Table } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
`;

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

// The 4 perspectives render as ONE continuous table (one shared sticky
// header) - perspective titles are injected as full-width rows within the
// same table via colSpan, rather than each perspective having its own
// separate Table/header.
const buildPlanningRows = (grouped) => {
  const rows = [];
  Object.entries(grouped).forEach(([perspective, items]) => {
    rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
    items.forEach((row) => rows.push({ ...row, key: `${perspective}-${row.sno}` }));
  });
  return rows;
};

const fullSpanCell = (record) => (record.isGroupHeader ? { colSpan: 5 } : {});
const hiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

const perspectiveColumns = [
  {
    title: "S.No",
    key: "sno",
    width: 80,
    onCell: fullSpanCell,
    render: (_, record) =>
      record.isGroupHeader ? (
        <div className="perspective-title m-0">{record.perspective}</div>
      ) : (
        record.sno
      ),
  },
  {
    title: "Measurement Dimensions",
    dataIndex: "measurementdimension",
    key: "measurementdimension",
    onCell: hiddenCell,
  },
  { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: hiddenCell },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 140, onCell: hiddenCell },
  {
    title: "Planning Assumptions / Comments",
    dataIndex: "comments",
    key: "comments",
    onCell: hiddenCell,
  },
];

const rolloverColumns = [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 80 },
  { title: "Objectives", dataIndex: "objectives", key: "objectives" },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 140 },
  { title: "Planning Assumptions / Comments", dataIndex: "comments", key: "comments" },
];

const softSkillsColumns = [
  { title: "Objectives", dataIndex: "objectives", key: "objectives" },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 140 },
];

const trainingColumns = [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
  { title: "Skill to be developed / strengthened", dataIndex: "skill", key: "skill" },
  {
    title: "Method of development, i.e. whether on-the-job , formal training or other.",
    dataIndex: "method",
    key: "method",
  },
  { title: "Planned / Expected quarter", dataIndex: "quarter", key: "quarter", width: 160 },
];

const groupByPerspective = (perspectives = []) => {
  const grouped = {};
  perspectives.forEach((item) => {
    const key = item.perspective || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
};

const ReviewKpi = () => {
  const history = useHistory();
  const location = useLocation();
  // Opened from My Team or the HR tab - Back / Done returns there.
  const backTab = location.state?.fromTab || "myteam";
  const contractid = location.state?.contractid;

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [popup, setPopup] = useState({ open: false, type: "success", message: "" });
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState("");

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while this page is mounted and restore after.
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  const fetchContract = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/contract/${contractid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setContract(data);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contractid) fetchContract();
  }, [contractid]);

  const respond = async (action, type, successMessage, payload = {}) => {
    setActionLoading(true);
    try {
      const { code } = await CALL_API(
        `performance-kpi-management/${action}/${contractid}`,
        "patch",
        payload
      );
      if (code === STATUS_CODE.SUCCESS) {
        setPopup({ open: true, type, message: successMessage });
        setTimeout(() => {
          setPopup((prev) => ({ ...prev, open: false }));
          history.push("/performance-kpi", { id: "kpiplanning", subTab: backTab });
        }, 1500);
      }
    } catch (error) {
      console.error(`Error trying to ${action} contract:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const onAccept = () => {
    Modal.confirm({
      title: "Remember, this can't be changed. Are you sure you want to accept this KPI?",
      icon: null,
      centered: true,
      okText: "Yes, Accept",
      cancelText: "No, Cancel",
      okButtonProps: { type: "primary" },
      onOk: () => respond("acceptkpireview", "success", "Accepted"),
    });
  };

  const onRequestRevision = () => {
    setRevisionReason("");
    setRevisionModalOpen(true);
  };

  const onSubmitRevisionReason = () => {
    setRevisionModalOpen(false);
    respond("requestrevision", "decline", "Revision Requested", {
      declinereason: revisionReason,
    });
  };

  const grouped = groupByPerspective(contract?.perspectives);
  const canRespond = contract?.planningstatus === "awaitingkpireview";

  return (
    <Wrap>
      <Spin spinning={loading}>
        <div className="breadcrumb mb-3">
          <p
            className="lite-text p-0 m-0"
            style={{ cursor: "pointer" }}
            onClick={() => history.push("/performance-kpi", { id: "kpiplanning", subTab: backTab })}
          >
            Performance Management
          </p>
          &nbsp;
          <RightOutlined className="mt-1" />
          &nbsp;
          <p className="m-0 p-0">Reviewing KPI</p>
        </div>

        <h6>Reviewing KPI</h6>

        <Row gutter={[16, 16]} className="my-3">
          <Col xs={24} md={6}>
            <div className="lite-text">Name of the employee</div>
            <div className="text-bold">{contract?.fullname || "-"}</div>
          </Col>
          <Col xs={24} md={6}>
            <div className="lite-text">Period</div>
            <div className="text-bold">{contract?.period ? `${contract.period} Appraisal` : "-"}</div>
          </Col>
          <Col xs={24} md={6}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(contract?.startdate)}</div>
          </Col>
          <Col xs={24} md={6}>
            <div className="lite-text">Progress Review</div>
            <div className="text-bold">
              {contract?.progressreviewdates && contract?.progressreviewdates.length > 0
                ? contract.progressreviewdates.map((item, idx) => (
                    <div key={idx}>
                      {item.label} – {formatDate(item.date)}
                    </div>
                  ))
                : "No"}
            </div>
          </Col>
        </Row>

        <h6 className="mt-4">Planning</h6>
        <p className="lite-text">
          Describe specific Objectives or other developmental activities to be
          undertaken by Appraisee during the appraisal period.
        </p>

        <Table
          columns={perspectiveColumns}
          dataSource={buildPlanningRows(grouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {(contract?.rolloverresponsibilities || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Rollover Responsibility from Previous Appraisal</div>
            <Table
              columns={rolloverColumns}
              dataSource={contract.rolloverresponsibilities}
              pagination={false}
              rowKey={(record) => `rollover-${record.sno}`}
            />
          </>
        )}

        <div className="perspective-title mt-4">Individual (Soft Skills) Assessment</div>
        <Table
          columns={softSkillsColumns}
          dataSource={contract?.softskillsassessment || []}
          pagination={false}
          rowKey={(record) => `softskills-${record.objectives}`}
        />

        {(contract?.trainingdevelopmentplan || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <p className="lite-text">
              Based on the strengths and limitations identified in the previous review, indicate the
              work experience and/or training suggested during the plan period.
            </p>
            <Table
              columns={trainingColumns}
              dataSource={contract.trainingdevelopmentplan}
              pagination={false}
              rowKey={(record) => `training-${record.sno}`}
            />
          </>
        )}

        {contract?.appraiseecomment && (
          <>
            <div className="perspective-title mt-4">Appraisee's Comment</div>
            <p>{contract.appraiseecomment}</p>
          </>
        )}

        {canRespond && (
          <div className="footer">
            <ButtonOutlined onClick={() => history.push("/performance-kpi", { id: "kpiplanning", subTab: backTab })}>
              Cancel
            </ButtonOutlined>
            <ButtonOutlined loading={actionLoading} onClick={onRequestRevision}>
              Request Revision
            </ButtonOutlined>
            <ButtonFilled type="primary" loading={actionLoading} onClick={onAccept}>
              Accept
            </ButtonFilled>
          </div>
        )}
      </Spin>

      <Modal
        title="Reason for requesting revision"
        open={revisionModalOpen}
        onCancel={() => setRevisionModalOpen(false)}
        centered
        footer={[
          <ButtonOutlined key="cancel" onClick={() => setRevisionModalOpen(false)}>
            Cancel
          </ButtonOutlined>,
          <ButtonFilled
            key="submit"
            type="primary"
            loading={actionLoading}
            onClick={onSubmitRevisionReason}
          >
            Request Revision
          </ButtonFilled>,
        ]}
      >
        <Input.TextArea
          rows={5}
          maxLength={250}
          value={revisionReason}
          onChange={(e) => setRevisionReason(e.target.value)}
        />
        <p className="lite-text mt-1">Maximum 250 characters</p>
      </Modal>

      <SuccessDeclinePopup
        open={popup.open}
        setOpen={(open) => setPopup((prev) => ({ ...prev, open }))}
        type={popup.type}
        message={popup.message}
      />
    </Wrap>
  );
};

export default ReviewKpi;
